import apiClient from './api';
import type { Product, CreateProductRequest, UpdateProductRequest, ProductSearchParams } from '../types/product';
import type { ImportColumns, ImportPreview, ImportResult } from '../types/import';
import { excelImportClient } from './excelImportClient';
import type { PagedResponse } from '../types/common';
import type { ProductAvailability } from '../types/stock';

const PRODUCT_IMPORT_PATH = '/products/import';

export const productService = {
  // Search products with filters and pagination
  search: async (companyId: number, params?: ProductSearchParams): Promise<PagedResponse<Product>> => {
    const response = await apiClient.get('/products', {
      params: { ...params, companyId },
    });
    return response.data;
  },

  // Get all products for a company (legacy support, fetches first page)
  // TODO: Update callers to use search() with pagination
  getAllByCompany: async (companyId: number): Promise<Product[]> => {
    const response = await apiClient.get('/products', {
      params: { companyId, size: 1000 }, // Fetch large page to simulate "all"
    });

    // Handle PagedResponse
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    }

    // Fallback if API structure is different
    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }

    return [];
  },

  // Get product by ID
  getById: async (id: number): Promise<Product> => {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
  },

  // Create new product
  create: async (productData: CreateProductRequest): Promise<Product> => {
    const response = await apiClient.post('/products', productData);
    return response.data;
  },

  // Update product
  update: async (id: number, productData: UpdateProductRequest): Promise<Product> => {
    const response = await apiClient.put(`/products/${id}`, productData);
    return response.data;
  },

  // Soft delete product
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/products/${id}`);
  },

  // Hard delete product
  hardDelete: async (id: number): Promise<void> => {
    await apiClient.delete(`/products/${id}/hard`);
  },

  // Restore an archived product
  restore: async (id: number): Promise<void> => {
    await apiClient.post(`/products/${id}/restore`);
  },

  // Import products from an .xlsx file
  previewImport: (file: File): Promise<ImportPreview> => excelImportClient.preview(PRODUCT_IMPORT_PATH, file),

  importFromExcel: (companyId: number, file: File, columns: ImportColumns): Promise<ImportResult> =>
    excelImportClient.run(PRODUCT_IMPORT_PATH, companyId, file, columns),

  // Get product availability across stocks
  getAvailability: async (companyId: number, productIds: number[]): Promise<ProductAvailability[]> => {
    const response = await apiClient.get(`/products/companies/${companyId}/availability`, {
      params: {
        productIds
      },
    });
    return response.data;
  },
};
